import React from "react";
import {
    Box, Flex, Stack, Text, Badge, IconButton, Icon, Tooltip,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, useColorModeValue,
} from "@chakra-ui/react";
import { MdAddCircleOutline, MdDeleteOutline } from "react-icons/md";
import SectionEditor from "./SectionEditor";
import JsonImportPanel from "./JsonImportPanel";

/**
 * Creates a new empty section with the given number.
 * @param {number} number - The section number
 * @returns {object} Empty section object
 */
function createEmptySection(number) {
    return { number: String(number), title: "", text: "", subsections: [] };
}

/**
 * Checks if a section number exists in the original sections array.
 * @param {string} sectionNumber - The section number to check
 * @param {Array|null} originalSections - Original sections for comparison
 * @returns {boolean} True if the section is new (not in original)
 */
function isSectionNew(sectionNumber, originalSections) {
    if (!originalSections) return false;
    return !originalSections.some(s => s.number === sectionNumber);
}

/**
 * Renders the accordion header row (title, NEW badge, remove button).
 * @param {object} props
 * @param {object} props.section - Section data with number and title
 * @param {boolean} props.isNew - Whether the section is newly added
 * @param {function} props.onRemove - Callback to remove the section
 * @returns {React.ReactElement}
 */
function SectionAccordionHeader({ section, isNew, onRemove }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex align="center">
            <AccordionButton flex="1">
                <Stack direction="row" spacing="8px" align="center" flex="1" textAlign="left">
                    <Text fontSize="0.85rem" fontWeight="600" color={titleColor}>
                        {section.number}. {section.title || "Untitled section"}
                    </Text>
                    {isNew && (
                        <Badge bg="rgba(76,175,80,0.15)" color="#4caf50" borderRadius="full" px="6px"
                            fontSize="0.65rem" fontWeight="700" textTransform="none">NOUVEAU</Badge>
                    )}
                </Stack>
                <AccordionIcon />
            </AccordionButton>
            <Tooltip label="Remove section">
                <IconButton aria-label="Supprimer la section" size="xs" variant="ghost" color="red.400" mr="8px"
                    icon={<Icon as={MdDeleteOutline} />} onClick={onRemove} />
            </Tooltip>
        </Flex>
    );
}

/**
 * Renders the empty state when no content sections exist.
 * @returns {React.ReactElement}
 */
function EmptySectionsPlaceholder() {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box textAlign="center" py="32px" border="1px dashed" borderColor="whiteAlpha.300" borderRadius="12px">
            <Text color={color} fontSize="0.85rem">No sections yet. Click + or import JSON to add content.</Text>
        </Box>
    );
}

/**
 * Renders the header row with section count and add button.
 * @param {object} props
 * @param {number} props.count - Number of content sections
 * @param {function} props.onAdd - Callback to add a new section
 * @returns {React.ReactElement}
 */
function ContentEditorHeader({ count, onAdd }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex justify="space-between" align="center" mb="8px">
            <Text fontSize="0.85rem" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px" color={labelColor}>
                Content Sections ({count})
            </Text>
            <Tooltip label="Add section">
                <IconButton aria-label="Ajouter une section" size="sm" variant="ghost"
                    icon={<Icon as={MdAddCircleOutline} />} onClick={onAdd} />
            </Tooltip>
        </Flex>
    );
}

/**
 * Renders the list of section accordions with editors inside.
 * @param {object} props
 * @param {Array} props.sections - Content sections array
 * @param {Array|null} props.originalSections - Original sections for new badge comparison
 * @param {function} props.onSectionChange - Handler for section updates (index, data)
 * @param {function} props.onRemove - Handler for section removal (index)
 * @returns {React.ReactElement}
 */
function SectionAccordionList({ sections, originalSections, onSectionChange, onRemove }) {
    const defaultIndex = sections.length <= 3 ? sections.map((_, i) => i) : [];
    const boxBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    return (
        <Accordion allowMultiple defaultIndex={defaultIndex}>
            {sections.map((section, idx) => (
                <AccordionItem key={idx} border="none" bg={boxBg} borderRadius="8px" mb="8px">
                    <SectionAccordionHeader section={section}
                        isNew={isSectionNew(section.number, originalSections)} onRemove={() => onRemove(idx)} />
                    <AccordionPanel pt="0" pb="16px" px="16px">
                        <SectionEditor section={section} onSectionChange={(updated) => onSectionChange(idx, updated)} />
                    </AccordionPanel>
                </AccordionItem>
            ))}
        </Accordion>
    );
}

/**
 * Manages a list of JSONB content sections with add/remove/edit and JSON import.
 * Shows "NOUVEAU" badge on sections not present in the original content.
 * @param {object} props
 * @param {object} props.content - Content object with sections array
 * @param {function} props.onChange - Callback with updated content object
 * @param {Array|null} props.originalSections - Original sections for comparison (edit mode)
 * @returns {React.ReactElement}
 */
function ContentEditor({ content, onChange, originalSections }) {
    const sections = content?.sections || [];

    /** @param {number} index @param {object} updatedSection */
    const handleSectionChange = (index, updatedSection) => {
        onChange({ sections: sections.map((s, i) => (i === index ? updatedSection : s)) });
    };

    /** Appends a new empty section to the list. */
    const handleAddSection = () => onChange({ sections: [...sections, createEmptySection(sections.length + 1)] });

    /** @param {number} index */
    const handleRemoveSection = (index) => onChange({ sections: sections.filter((_, i) => i !== index) });

    /** @param {Array} importedSections */
    const handleJsonImport = (importedSections) => onChange({ sections: [...sections, ...importedSections] });

    return (
        <Box>
            <ContentEditorHeader count={sections.length} onAdd={handleAddSection} />
            <JsonImportPanel onImport={handleJsonImport} />
            <SectionAccordionList sections={sections} originalSections={originalSections}
                onSectionChange={handleSectionChange} onRemove={handleRemoveSection} />
            {sections.length === 0 && <EmptySectionsPlaceholder />}
        </Box>
    );
}

export default ContentEditor;
